"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = require("react");
const remotion_1 = require("remotion");
const calculate_scale_1 = require("./calculate-scale");
const error_boundary_1 = require("./error-boundary");
const player_css_classname_1 = require("./player-css-classname");
const PlayerControls_1 = require("./PlayerControls");
const use_hover_state_1 = require("./use-hover-state");
const use_playback_1 = require("./use-playback");
const use_player_1 = require("./use-player");
const browser_supports_fullscreen_1 = require("./utils/browser-supports-fullscreen");
const calculate_player_size_1 = require("./utils/calculate-player-size");
const is_node_1 = require("./utils/is-node");
const use_element_size_1 = require("./utils/use-element-size");
const PlayerUI = ({ controls, style, loop, autoPlay, allowFullscreen, inputProps, clickToPlay, showVolumeControls, mediaVolume, mediaMuted, setMediaMuted, setMediaVolume, }, ref) => {
    var _a, _b;
    const config = remotion_1.Internals.useUnsafeVideoConfig();
    const video = remotion_1.Internals.useVideo();
    const container = react_1.useRef(null);
    const hovered = use_hover_state_1.useHoverState(container);
    const canvasSize = use_element_size_1.useElementSize(container);
    const [hasPausedToResume, setHasPausedToResume] = react_1.useState(false);
    const [shouldAutoplay, setShouldAutoPlay] = react_1.useState(autoPlay);
    const [isFullscreen, setIsFullscreen] = react_1.useState(() => false);
    use_playback_1.usePlayback({ loop });
    const player = use_player_1.usePlayer();
    react_1.useEffect(() => {
        if (hasPausedToResume && !player.playing) {
            setHasPausedToResume(false);
            player.play();
        }
    }, [hasPausedToResume, player]);
    react_1.useEffect(() => {
        const { current } = container;
        if (!current) {
            return;
        }
        const onFullscreenChange = () => {
            setIsFullscreen(document.fullscreenElement === current ||
                document.webkitFullscreenElement === current);
        };
        document.addEventListener('fullscreenchange', onFullscreenChange);
        document.addEventListener('webkitfullscreenchange', onFullscreenChange);
        return () => {
            document.removeEventListener('fullscreenchange', onFullscreenChange);
            document.removeEventListener('webkitfullscreenchange', onFullscreenChange);
        };
    }, []);
    const toggle = react_1.useCallback(() => {
        if (player.playing) {
            player.pause();
        }
        else {
            player.play();
        }
    }, [player]);
    const requestFullscreen = react_1.useCallback(() => {
        if (!allowFullscreen) {
            throw new Error('allowFullscreen is false');
        }
        if (!browser_supports_fullscreen_1.browserSupportsFullscreen) {
            throw new Error('Browser doesnt support fullscreen');
        }
        if (!container.current) {
            throw new Error('No player ref found');
        }
        if (container.current.webkitRequestFullScreen) {
            container.current.webkitRequestFullScreen();
        }
        else {
            container.current.requestFullscreen();
        }
    }, [allowFullscreen]);
    const exitFullscreen = react_1.useCallback(() => {
        if (document.webkitExitFullscreen) {
            document.webkitExitFullscreen();
        }
        else {
            document.exitFullscreen();
        }
    }, []);
    react_1.useImperativeHandle(ref, () => {
        const methods = {
            play: player.play,
            pause: player.pause,
            toggle,
            getCurrentFrame: player.getCurrentFrame,
            seekTo: (f) => {
                if (player.playing) {
                    setHasPausedToResume(true);
                    player.pause();
                }
                player.seek(f);
            },
            isFullscreen: () => isFullscreen,
            requestFullscreen,
            exitFullscreen,
            getVolume: () => {
                if (mediaMuted) {
                    return 0;
                }
                return mediaVolume;
            },
            setVolume: (vol) => {
                if (typeof vol !== 'number') {
                    throw new TypeError(`setVolume() takes a number, got value of type ${typeof vol}`);
                }
                if (isNaN(vol)) {
                    throw new TypeError(`setVolume() got a number that is NaN. Volume must be between 0 and 1.`);
                }
                if (vol < 0 || vol > 1) {
                    throw new TypeError(`setVolume() got a number that is out of range. Must be between 0 and 1, got ${typeof vol}`);
                }
                setMediaVolume(vol);
            },
            isMuted: () => mediaMuted || mediaVolume === 0,
            mute: () => {
                setMediaMuted(true);
            },
            unmute: () => {
                setMediaMuted(false);
            },
        };
        return Object.assign(player.emitter, methods);
    });
    const VideoComponent = video ? video.component : null;
    const outerStyle = react_1.useMemo(() => {
        if (!config) {
            return {};
        }
        return {
            position: 'relative',
            overflow: 'hidden',
            ...calculate_player_size_1.calculatePlayerSize({
                compositionHeight: config.height,
                compositionWidth: config.width,
                currentSize: canvasSize,
                height: style === null || style === void 0 ? void 0 : style.height,
                width: style === null || style === void 0 ? void 0 : style.width,
            }),
            ...style,
        };
    }, [canvasSize, config, style]);
    const layout = react_1.useMemo(() => {
        if (!config || !canvasSize) {
            return null;
        }
        return calculate_scale_1.calculateScale({
            canvasSize,
            compositionHeight: config.height,
            compositionWidth: config.width,
            previewSize: 'auto',
        });
    }, [canvasSize, config]);
    const outer = react_1.useMemo(() => {
        if (!layout || !config) {
            return {};
        }
        const { centerX, centerY, scale } = layout;
        return {
            width: config.width * scale,
            height: config.height * scale,
            display: 'flex',
            flexDirection: 'column',
            position: 'absolute',
            left: centerX,
            top: centerY,
            overflow: 'hidden',
        };
    }, [config, layout]);
    const containerStyle = react_1.useMemo(() => {
        if (!config || !canvasSize) {
            return {};
        }
        const { scale, xCorrection, yCorrection } = calculate_scale_1.calculateScale({
            canvasSize,
            compositionHeight: config.height,
            compositionWidth: config.width,
            previewSize: 'auto',
        });
        return {
            position: 'absolute',
            width: config.width,
            height: config.height,
            display: 'flex',
            transform: `scale(${scale})`,
            marginLeft: xCorrection,
            marginTop: yCorrection,
            overflow: 'hidden',
        };
    }, [canvasSize, config]);
    const onError = react_1.useCallback((error) => {
        player.pause();
        // Pay attention to `this context`
        player.emitter.dispatchError(error);
    }, [player]);
    const onFullscreenButtonClick = react_1.useCallback((e) => {
        e.stopPropagation();
        requestFullscreen();
    }, [requestFullscreen]);
    const onExitFullscreenButtonClick = react_1.useCallback((e) => {
        e.stopPropagation();
        exitFullscreen();
    }, [exitFullscreen]);
    const onSingleClick = react_1.useCallback(() => {
        toggle();
    }, [toggle]);
    react_1.useEffect(() => {
        if (shouldAutoplay) {
            player.play();
            setShouldAutoPlay(false);
        }
    }, [shouldAutoplay, player]);
    if (!config) {
        return null;
    }
    const content = (jsx_runtime_1.jsxs("div", Object.assign({ ref: container, style: outerStyle }, { children: [jsx_runtime_1.jsx("div", Object.assign({ style: outer, onClick: clickToPlay ? onSingleClick : undefined }, { children: jsx_runtime_1.jsx("div", Object.assign({ style: containerStyle, className: player_css_classname_1.PLAYER_CSS_CLASSNAME }, { children: VideoComponent ? (jsx_runtime_1.jsx(error_boundary_1.ErrorBoundary, Object.assign({ onError: onError }, { children: jsx_runtime_1.jsx(VideoComponent, Object.assign({}, ((_a = video === null || video === void 0 ? void 0 : video.props) !== null && _a !== void 0 ? _a : {}), ((_b = inputProps) !== null && _b !== void 0 ? _b : {})), void 0) }), void 0)) : null }), void 0) }), void 0),
            controls ? (jsx_runtime_1.jsx(PlayerControls_1.Controls, { fps: config.fps, durationInFrames: config.durationInFrames, hovered: hovered, player: player, onFullscreenButtonClick: onFullscreenButtonClick, isFullscreen: isFullscreen, allowFullscreen: allowFullscreen, showVolumeControls: showVolumeControls, onExitFullscreenButtonClick: onExitFullscreenButtonClick }, void 0)) : null] }), void 0));
    // Don't render suspense on Node.js
    if (is_node_1.IS_NODE) {
        return content;
    }
    return jsx_runtime_1.jsx(react_1.Suspense, Object.assign({ fallback: jsx_runtime_1.jsx("h1", { children: "Loading..." }, void 0) }, { children: content }), void 0);
};
exports.default = react_1.forwardRef(PlayerUI);
//# sourceMappingURL=PlayerUI.js.map